import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Settings as SettingsIcon, Loader2, KeyRound, Save, Sparkles, CheckCircle2, ExternalLink, Power } from "lucide-react";

const MODELS = [
  "openai/gpt-4o-mini",
  "openai/gpt-4o",
  "anthropic/claude-3.5-sonnet",
  "google/gemini-flash-1.5",
  "meta-llama/llama-3.1-70b-instruct",
];

export default function Settings() {
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [data, setData] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("openai/gpt-4o-mini");
  const [saving, setSaving] = useState(false);
  const [cap, setCap] = useState("");

  const load = useCallback(() => {
    if (!ready) return;
    const q = scopeParam ? `?${scopeParam}` : "";
    api.get(`/settings${q}`).then(({ data }) => {
      setData(data);
      setModel(data.openrouter_model || "openai/gpt-4o-mini");
      setApiKey("");
      setCap(String(data.ai_monthly_token_cap ?? ""));
    }).catch(() => setData({ has_key: false }));
  }, [scopeParam, ready]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const q = scopeParam ? `?${scopeParam}` : "";
      const payload = { openrouter_model: model };
      if (apiKey.trim()) payload.openrouter_api_key = apiKey.trim();
      await api.put(`/settings${q}`, payload);
      toast.success("AI settings saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const savePlatform = async (patch) => {
    setSaving(true);
    try {
      const q = scopeParam ? `?${scopeParam}` : "";
      await api.put(`/settings${q}`, patch);
      toast.success("Platform AI settings saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearKey = async () => {
    setSaving(true);
    try {
      const q = scopeParam ? `?${scopeParam}` : "";
      await api.put(`/settings${q}`, { openrouter_api_key: "" });
      toast.success("API key removed");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6 max-w-2xl">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">AI detection engine</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">AI Settings</h1>
          </div>
          <ClientPicker />
        </div>

        {data === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : (
          <>
            <div className={`rounded-2xl border p-4 flex items-start gap-3 ${data.ai_source !== "none" ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`} data-testid="ai-status">
              {data.ai_source !== "none" ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" /> : <Sparkles className="w-5 h-5 text-amber-600 mt-0.5" />}
              <div>
                <p className={`text-sm font-semibold ${data.ai_source !== "none" ? "text-green-700" : "text-amber-700"}`}>
                  {data.ai_source === "client" && "Your OpenRouter key is active — live AI detection on"}
                  {data.ai_source === "platform" && "COMO AI is active — included with your plan"}
                  {data.ai_source === "none" && (data.ai_reason === "cap_reached"
                    ? "Monthly AI allowance used up — using the built-in heuristic detector"
                    : "AI is off — using the built-in heuristic detector")}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data.ai_source !== "none"
                    ? "Comments are analysed in English, Tagalog, Bisaya and Ilokano, and your plans are written by AI."
                    : data.ai_reason === "cap_reached"
                      ? "It resets next month. Ask Bloom Dev Labs to raise your allowance."
                      : "Detection still works via a keyword/language heuristic. Ask Bloom Dev Labs to switch on COMO AI for your account."}
                </p>
                <p className="text-xs text-slate-400 mt-1" data-testid="ai-usage">
                  This month: {data.ai_usage?.tokens?.toLocaleString() ?? 0} of {data.ai_monthly_token_cap?.toLocaleString()} tokens · {data.ai_usage?.calls ?? 0} AI calls
                </p>
              </div>
            </div>

            {isSuper && (
              <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-6 space-y-4" data-testid="platform-ai">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Platform AI for this client</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {data.ai_platform_available
                        ? "Uses Bloom Dev Labs' key, counted against the monthly allowance below."
                        : "No platform key on this server yet. Set OPENROUTER_PLATFORM_KEY and restart."}
                    </p>
                  </div>
                  <button onClick={() => savePlatform({ ai_enabled: !data.ai_enabled })} disabled={saving} data-testid="toggle-platform-ai"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60 ${data.ai_enabled ? "bg-green-600 hover:bg-green-700 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`}>
                    <Power className="w-4 h-4" /> {data.ai_enabled ? "On" : "Off"}
                  </button>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Monthly token allowance</label>
                    <input type="number" min="0" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="ai-cap"
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-card text-sm focus:outline-none focus:ring-2 focus:ring-brass-500/40" />
                  </div>
                  <button onClick={() => savePlatform({ ai_monthly_token_cap: Math.max(0, parseInt(cap || "0", 10)) })} disabled={saving} data-testid="save-cap"
                    className="px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm disabled:opacity-60">Save</button>
                </div>
              </div>
            )}

            {isSuper && (
            <div className="bg-card rounded-2xl border border-slate-100 shadow-card p-6 space-y-5">
              <p className="text-xs text-slate-500">Optional: a client-owned OpenRouter key replaces the platform key and is not capped.</p>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">OpenRouter API key</label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                    placeholder={data.has_key ? "•••••••••• (saved — enter to replace)" : "sk-or-v1-..."} data-testid="openrouter-key"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-card text-sm focus:outline-none focus:ring-2 focus:ring-brass-500/40" />
                </div>
                <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-indigo-500 hover:text-indigo-600 mt-1.5">
                  Get an OpenRouter key <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Model</label>
                <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="openrouter-model"
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-card text-sm focus:outline-none focus:ring-2 focus:ring-brass-500/40">
                  {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-50">
                {data.has_key ? (
                  <button onClick={clearKey} disabled={saving} data-testid="clear-key" className="text-xs font-semibold text-red-500 hover:text-red-600">Remove key</button>
                ) : <span />}
                <button onClick={save} disabled={saving} data-testid="save-settings"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                </button>
              </div>
            </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
